/**
 * Pure function: takes section values + wrapper -> full HTML body string.
 * - greeting / ctaLabel / closingText are plain text and get HTML-escaped here.
 * - bodyParagraphs are sanitized HTML fragments coming from the rich editor;
 *   they are inline-styled for email-client compatibility (no <style>/<class>).
 * - {{variable}} tokens are preserved verbatim so the backend mailer can swap them.
 */

import { wrapEmailHtml } from './emailWrapperTemplate';
import type { EditableTemplateType } from './templateSections';
import { TEMPLATE_CONFIGS } from './templateSections';
import { sanitizeEmailHtml } from './emailHtmlSanitizer';

export interface SectionValues {
  greeting: string;
  /** Sanitized HTML fragments (no <p> wrapper). May contain {{variable}} tokens. */
  bodyParagraphs: string[];
  ctaLabel: string;
  closingText: string;
}

/** HTML-escape plain text while preserving {{variable}} tokens. */
function escapePlainPreservingTokens(text: string): string {
  const tokens: string[] = [];
  const stashed = text.replace(/\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g, (_m, name) => {
    tokens.push(name);
    return `__VAR_${tokens.length - 1}__`;
  });
  const escaped = stashed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
  return escaped.replace(/__VAR_(\d+)__/g, (_m, i) => `{{${tokens[parseInt(i)]}}}`);
}

const LINK_STYLE = 'color:#4339A0;text-decoration:underline;';
const UL_STYLE = 'margin:0 0 16px 20px;padding:0;';
const OL_STYLE = 'margin:0 0 16px 20px;padding:0;';
const LI_STYLE = 'margin:0 0 6px 0;font-size:16px;line-height:1.6;color:#333333;font-family:Arial,Helvetica,sans-serif;';

/**
 * Walks text nodes inside root and replaces runs of 2+ regular spaces with
 * a single space followed by NBSPs (U+00A0), so email clients (which collapse
 * whitespace) preserve what the user typed.
 */
function preserveSpacesInTextNodes(root: Element, doc: Document): void {
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  // Collect first to avoid mutating during traversal.
  const targets: Text[] = [];
  while ((n = walker.nextNode())) {
    if (n.nodeValue && / {2,}/.test(n.nodeValue)) targets.push(n as Text);
  }
  for (const t of targets) {
    t.nodeValue = t.nodeValue!.replace(/ {2,}/g, (m) => ' ' + ' '.repeat(m.length - 1));
  }
}

function inlineEmailStyles(fragmentHtml: string): string {
  const doc = new DOMParser().parseFromString(`<div id="__root">${fragmentHtml}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return fragmentHtml;

  preserveSpacesInTextNodes(root, doc);

  root.querySelectorAll('a').forEach((a) => {
    const existing = a.getAttribute('style') || '';
    a.setAttribute('style', `${LINK_STYLE}${existing}`);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  root.querySelectorAll('ul').forEach((ul) => {
    ul.setAttribute('style', UL_STYLE + (ul.getAttribute('style') || ''));
  });
  root.querySelectorAll('ol').forEach((ol) => {
    ol.setAttribute('style', OL_STYLE + (ol.getAttribute('style') || ''));
  });
  root.querySelectorAll('li').forEach((li) => {
    li.setAttribute('style', LI_STYLE + (li.getAttribute('style') || ''));
  });

  return root.innerHTML;
}

const P_STYLE = 'margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: #333333; font-family: Arial, Helvetica, sans-serif;';

/**
 * Detects if a fragment is just plain text (no tags). Plain text gets <br/>
 * for newlines for backwards-compatibility with old templates.
 */
function fragmentToParagraphHtml(fragment: string): string {
  if (!fragment.trim()) return '';
  const sanitized = sanitizeEmailHtml(fragment);
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(sanitized);
  const inner = looksLikeHtml
    ? inlineEmailStyles(sanitized)
    : escapePlainPreservingTokens(sanitized)
        .replace(/ {2,}/g, (m) => ' ' + ' '.repeat(m.length - 1))
        .replace(/\n/g, '<br/>');
  return `<p style="${P_STYLE}">${inner}</p>`;
}

export function assembleEmailHtml(
  templateType: EditableTemplateType,
  sections: SectionValues
): string {
  const config = TEMPLATE_CONFIGS[templateType];
  const ctaLink = config.ctaLinkVariable ? `{{${config.ctaLinkVariable}}}` : '';

  const bodyContent = sections.bodyParagraphs
    .map(fragmentToParagraphHtml)
    .filter(Boolean)
    .join('\n');

  return wrapEmailHtml({
    greeting: escapePlainPreservingTokens(sections.greeting),
    bodyContent,
    ctaLabel: escapePlainPreservingTokens(sections.ctaLabel),
    ctaLink,
    closingText: escapePlainPreservingTokens(sections.closingText),
  });
}

export function replaceVariablesWithSamples(
  html: string,
  templateType: EditableTemplateType
): string {
  const config = TEMPLATE_CONFIGS[templateType];
  let result = html;

  for (const variable of config.variables) {
    result = result.replace(
      new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g'),
      variable.sampleValue
    );
  }

  result = result.replace(/\{\{magicLink\}\}/g, 'https://app.clareefai.com/magic-link?token=abc123');
  result = result.replace(/\{\{loginLink\}\}/g, 'https://app.clareefai.com/login?token=abc123');
  result = result.replace(/\{\{surveyLink\}\}/g, 'https://promoter.clareefai.com/survey?token=abc123');
  result = result.replace(/\{\{vendorLogoHtml\}\}/g, '');
  result = result.replace(/\{\{companyAvatarHtml\}\}/g, '');
  result = result.replace(/\{\{rewardsListHtml\}\}/g, '');
  result = result.replace(/\{\{promoterListHtml\}\}/g, '');

  return result;
}
